import React, { useContext, useEffect, useState } from 'react'
import { Actions } from '../../components/turns/Actions';
import { Estado } from '../../components/turns/Estado';
import { AlertContext } from '../../contexts/AlertContext';
import { LoaderContext } from '../../contexts/LoaderContext';
import { convertirLetraMayus } from '../../helpers/convertirLetraMayus';
import { diferenciaHora, formatearFecha } from '../../helpers/fechaHoras';
import { SocketContext } from '../../contexts/SocketContext';
import { Contador } from '../../components/turns/Contador';
import { ModalsContext } from '../../contexts/ModalsContext';
import dayjs from 'dayjs';
import { useModalStore } from '../../store/useModalStore';

const columns = [
    { id: "turno", label: "Turno", minWidth: 170, align: 'center' },
    { id: "tramite", label: "Trámite", minWidth: 100, align: 'center' },
    { id: "creado", label: "Fecha", minWidth: 170, align: 'center' },
    { id: "espera", label: "Espera", minWidth: 170, align: 'center' },
    { id: "sede", label: "Sede", minWidth: 170, align: 'center' },
    { id: "acciones", label: "Acciones", minWidth: 170, align: 'center' }
];

export const useActualTurns = (sede) => {

    //*STATES
    const [total, setTotal] = useState(0);
    const [actualTurns, setActualTurns] = useState([]);

    //*CONTEXTS
    const { setOpenLoader } = useContext(LoaderContext);
    const { alertRef } = useContext(AlertContext);
    const { socket } = useContext(SocketContext);
    // const { modalRef } = useContext(ModalsContext);
    const { closeModal } = useModalStore();


    useEffect(() => {
        let unsubscribed = true;
        setOpenLoader(true);
        setActualTurns([]);

        unsubscribed && obtenerTurnos();

        return () => {
            unsubscribed = false;
            socket.off('frontend:obtener-turnos');
        }
    }, [sede]);

    useEffect(() => {
        let unsubscribed = true;

        unsubscribed && refrescarTurnos();

        return () => {
            unsubscribed = false;
            socket.off("backend:refrescar-turnos");
        };
    }, [sede]);

    useEffect(() => {
        let unsubscribed = true;

        unsubscribed && handleRefreshTurnsDeleted();

        return () => {
            unsubscribed = false;
            socket.off("refrescar-turnos-eliminados");
        };
    }, [sede]);

    useEffect(() => {
        let unsubscribed = true;

        unsubscribed && handleRefreshTurnsPriority();

        return () => {
            unsubscribed = false;
            socket.off("refrescar-turnos-priorizados");
        };
    }, [sede]);

    //*OBTENER TURNOS - ESPERA
    const obtenerTurnos = () => {
        socket.emit('frontend:obtener-turnos', { sede, estado: 1 }, response => {
            const { ok, message, total, data } = response;
    
            if(!ok) {
                setOpenLoader(false);
                alertRef.current.handleOpen('error', message);
                return;
            }
    
            
            setTotal(total);
            setActualTurns(data?.map(construirPayload));
            setOpenLoader(false);
        });
    }

    // //*REFRESCAR TURNOS ESTADO 1
    const refrescarTurnos = () => {
        socket.on('backend:refrescar-turnos', (response) => {
            const { ok, message, total, data } = response;
    
            if(!ok) {
                setOpenLoader(false);
                alertRef.current.handleOpen('error', message);
                return;
            }

            if(data?.sede?._id !== sede) return;
    
            if(data?.estado === 1) {
                setTotal(e => e + total);
                agregarTurnoAlArreglo(data);
                return;
            } 

            if(data?.estado === 2) {
                eliminarTurnoDelArreglo(data);
                setTotal(e => e - 1);
                return;
            }
        });
    }

    //*REFRESCAR TURNOS PRIORIZADOS - ESPERA
    const handleRefreshTurnsPriority = () => socket.on('refrescar-turnos-priorizados', (response) => {
        const { data } = response;

        if(data?.sede?._id !== sede) return;

        actualizarTurnoDelArreglo(data);
    });

    //*PRIORIZAR TURNO - ESPERA
    const handlePriorityTurn = (uid, prioritario) => {
        setOpenLoader(true);

        const payload = {
            uid,
            prioritario: !prioritario
        }

        socket.emit('priorizar-turno', payload, (response) => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef.current.handleOpen('error', message);
                return;
            }

            actualizarTurnoDelArreglo(data);
            setOpenLoader(false);
        });
    }

    //*REFRESCAR TURNOS ELIMINADOS - ESPERA
    const handleRefreshTurnsDeleted = () => socket.on('refrescar-turnos-eliminados', (response) => {
        const { data } = response;

        if(data?.sede?._id !== sede) return;
        
        setTotal(e => e - 1);
        eliminarTurnoDelArreglo(data);
        setOpenLoader(false);
    });

    //*ELIMINAR TURNO - ESPERA
    const handleDeleteTurn = (args) => {
        setOpenLoader(true);

        const payload = {
            uid: args?.data?.uid,
            observacion: args?.observacion,
            tiempo_espera: diferenciaHora(args?.data?.creado, true),
        }
      
        socket.emit('eliminar-turno', payload, (response) => {
            const { ok, message, total, data } = response;

            if(!ok) {
                setOpenLoader(false);
                // modalRef?.current?.handleClose();
                closeModal();
                alertRef.current.handleOpen('error', message);
                return;
            }

            setTotal(e => e - 1);
            eliminarTurnoDelArreglo(data);
            
            // modalRef?.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        });
    }

    const agregarTurnoAlArreglo = (turno) => {
        setActualTurns(prev => ([ ...prev, construirPayload(turno) ]));
    }

    const actualizarTurnoDelArreglo = (turno) => {
        setActualTurns(prev => prev?.map(t => t?.uid === turno?.uid ? construirPayload(turno) : t));
    }

    const eliminarTurnoDelArreglo = (turno) => {
        setActualTurns(prev => prev?.filter(t => t?.uid !== turno?.uid));
    }

    const construirPayload = (turno) => ({
        uid: turno?.uid,
        turno: turno?.turno?.toUpperCase(),
        tramite: convertirLetraMayus(turno?.tramite?.nombre),
        creado: (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{ formatearFecha(turno?.creado) }</span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{ dayjs(turno?.creado).format('hh:mm:ss A') }</span>
            </div>
        ),
        espera: (
            <Estado estado={ turno?.estado } fecha={ turno?.creado } tiempo={ turno?.tramite?.tiempo_maximo_espera } data={ turno }>
                <Contador fecha={ turno?.creado } estado={ turno?.estado } style={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }} />
            </Estado>
        ) ,
        sede: convertirLetraMayus(turno?.sede?.nombre),
        acciones: <Actions data={ turno } priority={ handlePriorityTurn } deleted={ handleDeleteTurn } />
    })

    return {
        columns,
        total,
        actualTurns,
        handlePriorityTurn,
        handleDeleteTurn
    }
}